/**
 * PriceChart — reusable candlestick chart shared between AssetDetail and Analysis.
 *
 * Features: 5M/15M/30M/1H/1D/1W/1M/3M/1Y timeframes, volume histogram, SMA/EMA/Bollinger
 * overlays, indicator toggle menu, live price updates via WebSocket.
 */
import React, { useEffect, useRef, useState } from 'react';
import { createChart, IChartApi, ISeriesApi, Time, LineStyle } from 'lightweight-charts';
import { ChevronDown, Layers } from 'lucide-react';
import eodhd from '../services/eodhd';
import { useRealtimePrice } from '../hooks/useRealtimePrice';
import LoadingSpinner from './LoadingSpinner';

// ── Types ────────────────────────────────────────────────────────────────────

interface ChartDataPoint {
  time: Time;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

interface IndicatorDataPoint {
  time: Time;
  value: number;
}

interface IndicatorSettings {
  sma50: boolean;
  sma200: boolean;
  ema20: boolean;
  bollinger: boolean;
}

type Timeframe = '5M' | '15M' | '30M' | '1H' | '1D' | '1W' | '1M' | '3M' | '1Y';

const TIMEFRAMES: { id: Timeframe; label: string }[] = [
  { id: '5M',  label: '5M'  },
  { id: '15M', label: '15M' },
  { id: '30M', label: '30M' },
  { id: '1H',  label: '1H'  },
  { id: '1D',  label: '1D'  },
  { id: '1W',  label: '1W'  },
  { id: '1M',  label: '1M'  },
  { id: '3M',  label: '3M'  },
  { id: '1Y',  label: '1Y'  },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

function aggregateBars(bars: ChartDataPoint[], groupSize: number): ChartDataPoint[] {
  const result: ChartDataPoint[] = [];
  for (let i = 0; i < bars.length; i += groupSize) {
    const group = bars.slice(i, i + groupSize);
    if (group.length === 0) continue;
    result.push({
      time: group[0].time,
      open: group[0].open,
      high: Math.max(...group.map((b) => b.high)),
      low: Math.min(...group.map((b) => b.low)),
      close: group[group.length - 1].close,
      volume: group.reduce((sum, b) => sum + (b.volume || 0), 0),
    });
  }
  return result;
}

function calculateSMA(data: ChartDataPoint[], period: number): IndicatorDataPoint[] {
  const result: IndicatorDataPoint[] = [];
  for (let i = period - 1; i < data.length; i++) {
    let sum = 0;
    for (let j = 0; j < period; j++) sum += data[i - j].close;
    result.push({ time: data[i].time, value: sum / period });
  }
  return result;
}

function calculateEMA(data: ChartDataPoint[], period: number): IndicatorDataPoint[] {
  const result: IndicatorDataPoint[] = [];
  const multiplier = 2 / (period + 1);
  let ema = data[0].close;
  for (let i = 0; i < data.length; i++) {
    ema = (data[i].close - ema) * multiplier + ema;
    if (i >= period - 1) result.push({ time: data[i].time, value: ema });
  }
  return result;
}

function calculateBollingerBands(
  data: ChartDataPoint[],
  period: number,
  stdDev: number,
): { upper: IndicatorDataPoint[]; middle: IndicatorDataPoint[]; lower: IndicatorDataPoint[] } {
  const upper: IndicatorDataPoint[] = [];
  const middle: IndicatorDataPoint[] = [];
  const lower: IndicatorDataPoint[] = [];

  for (let i = period - 1; i < data.length; i++) {
    const slice = data.slice(i - period + 1, i + 1);
    const avg = slice.reduce((s, d) => s + d.close, 0) / period;
    const variance = slice.reduce((s, d) => s + Math.pow(d.close - avg, 2), 0) / period;
    const sd = Math.sqrt(variance);
    middle.push({ time: data[i].time, value: avg });
    upper.push({ time: data[i].time, value: avg + stdDev * sd });
    lower.push({ time: data[i].time, value: avg - stdDev * sd });
  }
  return { upper, middle, lower };
}

// ── Component ────────────────────────────────────────────────────────────────

interface PriceChartProps {
  symbol: string;
  /** Initial timeframe. Defaults to '1D'. */
  defaultTimeframe?: Timeframe;
  /** Chart height in px. Defaults to 320. */
  height?: number;
}

const PriceChart: React.FC<PriceChartProps> = ({
  symbol,
  defaultTimeframe = '1D',
  height = 320,
}) => {
  const fullSymbol = symbol.includes('.') ? symbol : `${symbol}.US`;

  // ── State ────────────────────────────────────────────────────────────────
  const [timeframe, setTimeframe] = useState<Timeframe>(defaultTimeframe);
  const [chartLoading, setChartLoading] = useState(true);
  const [isLiveChart, setIsLiveChart] = useState(false);
  const [showIndicatorMenu, setShowIndicatorMenu] = useState(false);
  const [indicatorSettings, setIndicatorSettings] = useState<IndicatorSettings>({
    sma50: true,
    sma200: true,
    ema20: false,
    bollinger: false,
  });

  // ── Refs ─────────────────────────────────────────────────────────────────
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const volumeSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const sma50SeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const sma200SeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const ema20SeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const bollingerUpperRef = useRef<ISeriesApi<'Line'> | null>(null);
  const bollingerMiddleRef = useRef<ISeriesApi<'Line'> | null>(null);
  const bollingerLowerRef = useRef<ISeriesApi<'Line'> | null>(null);
  const barDurationSecondsRef = useRef<number | null>(null);
  const lastCandleRef = useRef<ChartDataPoint | null>(null);

  // ── Live price ───────────────────────────────────────────────────────────
  const livePrice = useRealtimePrice(fullSymbol);

  const toggleIndicator = (key: keyof IndicatorSettings) => {
    setIndicatorSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // ── Data loading helpers ─────────────────────────────────────────────────
  const loadDailyBars = async (sym: string, days: number): Promise<ChartDataPoint[]> => {
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - days);
    const data = await eodhd.getHistoricalData(sym, {
      from: fromDate.toISOString().split('T')[0],
      to: toDate.toISOString().split('T')[0],
    });
    const scale = sym.endsWith('.JSE') ? 0.01 : 1;
    return (data || []).map((d) => ({
      time: d.date as Time,
      open: d.open * scale,
      high: d.high * scale,
      low: d.low * scale,
      close: d.close * scale,
      volume: d.volume,
    }));
  };

  const updateIndicatorOverlays = (chartData: ChartDataPoint[], settings: IndicatorSettings) => {
    if (chartData.length < 50) {
      sma50SeriesRef.current?.setData([]);
      sma200SeriesRef.current?.setData([]);
      ema20SeriesRef.current?.setData([]);
      bollingerUpperRef.current?.setData([]);
      bollingerMiddleRef.current?.setData([]);
      bollingerLowerRef.current?.setData([]);
      return;
    }

    sma50SeriesRef.current?.setData(
      settings.sma50 ? calculateSMA(chartData, 50) : [],
    );

    sma200SeriesRef.current?.setData(
      settings.sma200 && chartData.length >= 200 ? calculateSMA(chartData, 200) : [],
    );

    ema20SeriesRef.current?.setData(
      settings.ema20 ? calculateEMA(chartData, 20) : [],
    );

    if (settings.bollinger) {
      const bb = calculateBollingerBands(chartData, 20, 2);
      bollingerUpperRef.current?.setData(bb.upper);
      bollingerMiddleRef.current?.setData(bb.middle);
      bollingerLowerRef.current?.setData(bb.lower);
    } else {
      bollingerUpperRef.current?.setData([]);
      bollingerMiddleRef.current?.setData([]);
      bollingerLowerRef.current?.setData([]);
    }
  };

  const loadChartData = async (tf: Timeframe, settings: IndicatorSettings) => {
    if (!candleSeriesRef.current) return;

    try {
      setChartLoading(true);
      lastCandleRef.current = null;

      let chartData: ChartDataPoint[] = [];

      const intradayTfs = ['5M', '15M', '30M', '1H', '1D', '1W'] as const;
      type IntradayTF = typeof intradayTfs[number];

      if ((intradayTfs as readonly string[]).includes(tf)) {
        const nowSec = Math.floor(Date.now() / 1000);

        if (fullSymbol.endsWith('.JSE')) {
          // JSE: use stored IRESS 15-min bars from jse_bar_recorder
          const jseTfConfig: Record<IntradayTF, { windowDays: number; barSeconds: number; aggregateBy?: number }> = {
            '5M':  { windowDays: 5,  barSeconds: 900 },
            '15M': { windowDays: 10, barSeconds: 900 },
            '30M': { windowDays: 15, barSeconds: 1800, aggregateBy: 2 },
            '1H':  { windowDays: 30, barSeconds: 3600, aggregateBy: 4 },
            '1D':  { windowDays: 1,  barSeconds: 900 },
            '1W':  { windowDays: 7,  barSeconds: 900 },
          };
          const { windowDays, barSeconds, aggregateBy } = jseTfConfig[tf as IntradayTF];
          const fromSec = tf === '1D'
            ? (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return Math.floor(d.getTime() / 1000); })()
            : nowSec - windowDays * 24 * 60 * 60;

          const jseBars = await eodhd.getJseBars(fullSymbol, { from: fromSec, to: nowSec });

          if (jseBars && jseBars.length > 0) {
            const sorted = [...jseBars].sort((a, b) => a.timestamp - b.timestamp);
            let bars: ChartDataPoint[] = sorted.map((d) => ({
              time: d.timestamp as unknown as Time,
              open: d.open * 0.01,
              high: d.high * 0.01,
              low: d.low * 0.01,
              close: d.close * 0.01,
              volume: d.volume,
            }));
            if (aggregateBy) bars = aggregateBars(bars, aggregateBy);
            chartData = bars;
            barDurationSecondsRef.current = barSeconds;
          } else {
            barDurationSecondsRef.current = null;
            const fallback: Record<string, number> = { '5M': 5, '15M': 10, '30M': 15, '1H': 30, '1D': 5, '1W': 7 };
            chartData = await loadDailyBars(fullSymbol, fallback[tf] ?? 5);
          }
        } else {
          // Non-JSE: use EODHD intraday
          const tfConfig: Record<IntradayTF, { interval: string; windowDays: number; barSeconds: number; aggregateBy?: number }> = {
            '5M':  { interval: '5m', windowDays: 5,  barSeconds: 300 },
            '15M': { interval: '5m', windowDays: 10, barSeconds: 900,  aggregateBy: 3 },
            '30M': { interval: '5m', windowDays: 15, barSeconds: 1800, aggregateBy: 6 },
            '1H':  { interval: '1h', windowDays: 30, barSeconds: 3600 },
            '1D':  { interval: '5m', windowDays: 1,  barSeconds: 300 },
            '1W':  { interval: '1h', windowDays: 7,  barSeconds: 3600 },
          };
          const { interval, windowDays, barSeconds, aggregateBy } = tfConfig[tf as IntradayTF];
          const fromSec = tf === '1D'
            ? (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return Math.floor(d.getTime() / 1000); })()
            : nowSec - windowDays * 24 * 60 * 60;

          const intradayData = await eodhd.getIntradayData(fullSymbol, interval, { from: fromSec, to: nowSec });

          if (intradayData && intradayData.length > 0) {
            const sorted = [...intradayData].sort((a, b) => a.timestamp - b.timestamp);
            let bars: ChartDataPoint[] = sorted.map((d) => ({
              time: d.timestamp as unknown as Time,
              open: d.open,
              high: d.high,
              low: d.low,
              close: d.close,
              volume: d.volume,
            }));
            if (aggregateBy) bars = aggregateBars(bars, aggregateBy);
            chartData = bars;
            barDurationSecondsRef.current = barSeconds;
          } else {
            barDurationSecondsRef.current = null;
            const fallback: Record<string, number> = { '5M': 5, '15M': 10, '30M': 15, '1H': 30, '1D': 5, '1W': 7 };
            chartData = await loadDailyBars(fullSymbol, fallback[tf] ?? 5);
          }
        }
      } else {
        // Daily path: 1M / 3M / 1Y
        const days = tf === '1M' ? 30 : tf === '3M' ? 90 : 365;
        chartData = await loadDailyBars(fullSymbol, days);
        barDurationSecondsRef.current = null;
      }

      if (chartData.length > 0) {
        candleSeriesRef.current.setData(chartData);

        if (volumeSeriesRef.current) {
          volumeSeriesRef.current.setData(
            chartData.map((d, i) => ({
              time: d.time,
              value: d.volume || 0,
              color:
                d.close >= (i > 0 ? chartData[i - 1].close : d.open)
                  ? 'rgba(16, 185, 129, 0.5)'
                  : 'rgba(239, 68, 68, 0.5)',
            })),
          );
        }

        updateIndicatorOverlays(chartData, settings);
        chartRef.current?.timeScale().fitContent();
      }
    } catch (error) {
      console.error('[PriceChart] Error loading chart data:', error);
    } finally {
      setChartLoading(false);
    }
  };

  // ── Chart initialization ─────────────────────────────────────────────────
  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
    }
    if (!chartContainerRef.current) return;

    const init = () => {
      if (!chartContainerRef.current) return;

      chartRef.current = createChart(chartContainerRef.current, {
        layout: {
          background: { color: '#1E293B' },
          textColor: '#94A3B8',
        },
        grid: {
          vertLines: { color: '#334155' },
          horzLines: { color: '#334155' },
        },
        crosshair: { mode: 1 },
        rightPriceScale: { borderColor: '#334155' },
        timeScale: { borderColor: '#334155', timeVisible: true },
        width: chartContainerRef.current.clientWidth || 600,
        height,
      });

      candleSeriesRef.current = chartRef.current.addCandlestickSeries({
        upColor: '#10B981',
        downColor: '#EF4444',
        borderDownColor: '#EF4444',
        borderUpColor: '#10B981',
        wickDownColor: '#EF4444',
        wickUpColor: '#10B981',
      });

      // Volume histogram — bottom 15%
      volumeSeriesRef.current = chartRef.current.addHistogramSeries({
        color: '#26a69a',
        priceFormat: { type: 'volume' },
        priceScaleId: 'volume',
      });
      chartRef.current.priceScale('volume').applyOptions({ scaleMargins: { top: 0.85, bottom: 0 } });

      // SMA50 (blue)
      sma50SeriesRef.current = chartRef.current.addLineSeries({
        color: '#3B82F6', lineWidth: 1, lineStyle: LineStyle.Solid,
        priceLineVisible: false, lastValueVisible: false,
      });

      // SMA200 (orange)
      sma200SeriesRef.current = chartRef.current.addLineSeries({
        color: '#F97316', lineWidth: 1, lineStyle: LineStyle.Solid,
        priceLineVisible: false, lastValueVisible: false,
      });

      // EMA20 (purple)
      ema20SeriesRef.current = chartRef.current.addLineSeries({
        color: '#A855F7', lineWidth: 1, lineStyle: LineStyle.Solid,
        priceLineVisible: false, lastValueVisible: false,
      });

      // Bollinger Bands (cyan)
      bollingerUpperRef.current = chartRef.current.addLineSeries({
        color: 'rgba(34,211,238,0.6)', lineWidth: 1, lineStyle: LineStyle.Dashed,
        priceLineVisible: false, lastValueVisible: false,
      });
      bollingerMiddleRef.current = chartRef.current.addLineSeries({
        color: 'rgba(34,211,238,0.8)', lineWidth: 1, lineStyle: LineStyle.Dotted,
        priceLineVisible: false, lastValueVisible: false,
      });
      bollingerLowerRef.current = chartRef.current.addLineSeries({
        color: 'rgba(34,211,238,0.6)', lineWidth: 1, lineStyle: LineStyle.Dashed,
        priceLineVisible: false, lastValueVisible: false,
      });

      loadChartData(timeframe, indicatorSettings);
    };

    const timer = setTimeout(init, 50);

    const handleResize = () => {
      chartRef.current?.applyOptions({ width: chartContainerRef.current?.clientWidth ?? 600 });
    };
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
      chartRef.current?.remove();
      chartRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  // Reload on timeframe or indicator change
  useEffect(() => {
    if (candleSeriesRef.current) loadChartData(timeframe, indicatorSettings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeframe, indicatorSettings]);

  // Live price updates
  useEffect(() => {
    if (!candleSeriesRef.current || !livePrice.price || !livePrice.isLive) {
      setIsLiveChart(false);
      return;
    }
    setIsLiveChart(true);

    const currentPrice = Number(livePrice.price);
    let time: Time;

    if (barDurationSecondsRef.current !== null) {
      const nowSec = Math.floor(Date.now() / 1000);
      const dur = barDurationSecondsRef.current;
      time = (Math.floor(nowSec / dur) * dur) as unknown as Time;
    } else {
      time = new Date().toISOString().split('T')[0] as Time;
    }

    if (!lastCandleRef.current || lastCandleRef.current.time !== time) {
      lastCandleRef.current = { time, open: currentPrice, high: currentPrice, low: currentPrice, close: currentPrice };
    } else {
      lastCandleRef.current = {
        ...lastCandleRef.current,
        high: Math.max(lastCandleRef.current.high, currentPrice),
        low: Math.min(lastCandleRef.current.low, currentPrice),
        close: currentPrice,
      };
    }

    try { candleSeriesRef.current.update(lastCandleRef.current); } catch { /* ignore */ }
  }, [livePrice.price, livePrice.isLive]);

  // ── JSX ──────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Toolbar: timeframes + indicator menu + live badge */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        {/* Timeframe buttons */}
        <div className="flex gap-1 p-1 bg-slate-700 rounded-lg">
          {TIMEFRAMES.map((tf) => (
            <button
              key={tf.id}
              onClick={() => setTimeframe(tf.id)}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                timeframe === tf.id
                  ? 'bg-teal-500 text-slate-900'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              {tf.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          {/* Live badge */}
          {isLiveChart && (
            <span className="flex items-center gap-1 px-2 py-1 bg-emerald-500/10 border border-emerald-500/30 rounded text-[10px] font-semibold text-emerald-400 uppercase tracking-wide">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
              LIVE
            </span>
          )}

          {/* Indicators dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowIndicatorMenu((v) => !v)}
              className="flex items-center gap-2 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-xs transition-colors"
            >
              <Layers size={13} />
              Indicators
              <ChevronDown size={13} className={`transition-transform ${showIndicatorMenu ? 'rotate-180' : ''}`} />
            </button>
            {showIndicatorMenu && (
              <div className="absolute right-0 top-full mt-2 w-52 bg-slate-700 rounded-lg shadow-lg border border-slate-600 z-20 py-2">
                <div className="px-3 py-1 text-xs text-slate-400 uppercase font-semibold">Price Overlays</div>
                {(
                  [
                    { key: 'sma50',    label: 'SMA 50',         color: 'bg-blue-500'   },
                    { key: 'sma200',   label: 'SMA 200',        color: 'bg-orange-500' },
                    { key: 'ema20',    label: 'EMA 20',         color: 'bg-purple-500' },
                    { key: 'bollinger',label: 'Bollinger Bands', color: 'bg-cyan-400'   },
                  ] as const
                ).map(({ key, label, color }) => (
                  <button
                    key={key}
                    onClick={() => toggleIndicator(key)}
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-600 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-0.5 ${color} rounded`} />
                      <span className="text-sm">{label}</span>
                    </div>
                    <div className={`w-4 h-4 rounded border ${indicatorSettings[key] ? 'bg-teal-500 border-teal-500' : 'border-slate-500'}`}>
                      {indicatorSettings[key] && <span className="text-xs text-white leading-none">✓</span>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Active indicators legend */}
      {(indicatorSettings.sma50 || indicatorSettings.sma200 || indicatorSettings.ema20 || indicatorSettings.bollinger) && (
        <div className="flex flex-wrap gap-4 mb-2 text-xs">
          {indicatorSettings.sma50    && <div className="flex items-center gap-1.5"><div className="w-3 h-0.5 bg-blue-500 rounded" /><span className="text-slate-400">SMA 50</span></div>}
          {indicatorSettings.sma200   && <div className="flex items-center gap-1.5"><div className="w-3 h-0.5 bg-orange-500 rounded" /><span className="text-slate-400">SMA 200</span></div>}
          {indicatorSettings.ema20    && <div className="flex items-center gap-1.5"><div className="w-3 h-0.5 bg-purple-500 rounded" /><span className="text-slate-400">EMA 20</span></div>}
          {indicatorSettings.bollinger && <div className="flex items-center gap-1.5"><div className="w-3 h-0.5 bg-cyan-400 rounded" /><span className="text-slate-400">Bollinger Bands</span></div>}
        </div>
      )}

      {/* Chart canvas */}
      <div className="relative">
        {chartLoading && (
          <div className="absolute inset-0 bg-slate-800/80 flex items-center justify-center z-10 rounded-lg">
            <LoadingSpinner />
          </div>
        )}
        <div ref={chartContainerRef} className="w-full" style={{ minHeight: height }} />
      </div>
    </div>
  );
};

export default PriceChart;
